import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiImage, FiSave, FiEye, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { marked } from 'marked';
import { blogAPI } from '../services/blogAPI';
import { useAuth } from '../context/AuthContext';

marked.setOptions({ breaks: true, gfm: true });

const categories = [
  { name: 'WEIGHT_LOSS_TIPS', displayName: 'Weight Loss Tips' },
  { name: 'HEALTHY_RECIPES', displayName: 'Healthy Recipes' },
  { name: 'NUTRITION', displayName: 'Nutrition' },
  { name: 'EXERCISE', displayName: 'Exercise' },
  { name: 'GLP1_MEDICATION', displayName: 'GLP-1 Medication' },
  { name: 'MENTAL_HEALTH', displayName: 'Mental Health' },
  { name: 'SUCCESS_STORIES', displayName: 'Success Stories' },
  { name: 'LIFESTYLE', displayName: 'Lifestyle' },
  { name: 'SLEEP_WELLNESS', displayName: 'Sleep & Wellness' },
  { name: 'METABOLISM', displayName: 'Metabolism' },
];

const validationSchema = Yup.object({
  title: Yup.string()
    .min(5, 'Title must be at least 5 characters')
    .max(200, 'Title must not exceed 200 characters')
    .required('Title is required'),
  summary: Yup.string()
    .max(500, 'Summary must not exceed 500 characters'),
  content: Yup.string()
    .required('Content is required'),
  category: Yup.string()
    .required('Category is required'),
  featuredImage: Yup.string()
    .url('Must be a valid URL'),
  authorName: Yup.string(),
  authorTitle: Yup.string(),
  readTime: Yup.number()
    .min(1, 'Read time must be at least 1 minute')
    .max(60, 'Read time must not exceed 60 minutes'),
});

const AddBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: '',
    summary: '',
    content: '',
    category: '',
    featuredImage: '',
    authorName: '',
    authorTitle: '',
    authorImage: '',
    readTime: '',
    isPublished: true,
    isFeatured: false,
  });
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to create a blog post');
      navigate('/login');
      return;
    }

    if (id) {
      setIsEditMode(true);
      fetchBlog();
    } else {
      // Set default author name from logged in user
      setInitialValues(prev => ({
        ...prev,
        authorName: user ? `${user.firstName} ${user.lastName}` : '',
      }));
    }
  }, [id, isAuthenticated, user]);

  const fetchBlog = async () => {
    try {
      const response = await blogAPI.getBlogById(id);
      const blog = response.data;
      setInitialValues({
        title: blog.title || '',
        summary: blog.summary || '',
        content: blog.content || '',
        category: blog.category || '',
        featuredImage: blog.featuredImage || '',
        authorName: blog.authorName || '',
        authorTitle: blog.authorTitle || '',
        authorImage: blog.authorImage || '',
        readTime: blog.readTime || '',
        isPublished: blog.isPublished !== undefined ? blog.isPublished : true,
        isFeatured: blog.isFeatured || false,
      });
    } catch (error) {
      toast.error('Failed to fetch blog');
      navigate('/blog');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const blogData = {
        ...values,
        readTime: values.readTime ? parseInt(values.readTime) : null,
      };

      if (isEditMode) {
        await blogAPI.updateBlog(id, blogData);
        toast.success('Blog updated successfully!');
      } else {
        await blogAPI.createBlog(blogData);
        toast.success('Blog created successfully!');
      }
      navigate('/blog');
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error(error.response?.data?.message || 'Failed to save blog');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const sampleImages = [
    'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800',
    'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800',
    'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=800',
    'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800',
    'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800',
    'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800',
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft /> Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h1>
          </div>
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <FiEye /> {previewMode ? 'Edit' : 'Preview'}
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <Form>
              {previewMode ? (
                /* Preview Mode */
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  {values.featuredImage && (
                    <div className="aspect-video">
                      <img
                        src={values.featuredImage}
                        alt={values.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-8">
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-sky-100 text-sky-700 mb-4">
                      {categories.find(c => c.name === values.category)?.displayName || values.category}
                    </span>
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">{values.title || 'Untitled'}</h1>
                    {values.summary && (
                      <p className="text-lg text-gray-600 mb-6 italic">{values.summary}</p>
                    )}
                    <div className="flex items-center gap-4 text-gray-500 mb-8">
                      <span>{values.authorName || 'Anonymous'}</span>
                      {values.readTime && <span>{values.readTime} min read</span>}
                    </div>
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: marked(values.content || '') }} />
                  </div>
                </div>
              ) : (
                /* Edit Mode */
                <div className="space-y-6">
                  {/* Title */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <Field
                      type="text"
                      name="title"
                      placeholder="Enter an engaging title..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-lg"
                    />
                    <ErrorMessage name="title" component="p" className="mt-1 text-sm text-red-600" />
                  </div>

                  {/* Category */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <Field
                      as="select"
                      name="category"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat.name} value={cat.name}>
                          {cat.displayName}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="category" component="p" className="mt-1 text-sm text-red-600" />
                  </div>

                  {/* Featured Image */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FiImage className="inline mr-2" />
                      Featured Image URL
                    </label>
                    <Field
                      type="url"
                      name="featuredImage"
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    />
                    <ErrorMessage name="featuredImage" component="p" className="mt-1 text-sm text-red-600" />

                    {/* Sample Images */}
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-2">Or choose a sample image:</p>
                      <div className="grid grid-cols-6 gap-2">
                        {sampleImages.map((img, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setFieldValue('featuredImage', img)}
                            className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                              values.featuredImage === img ? 'border-sky-500' : 'border-transparent'
                            }`}
                          >
                            <img src={img} alt={`Sample ${index + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview */}
                    {values.featuredImage && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">Preview:</p>
                        <img
                          src={values.featuredImage}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Summary
                    </label>
                    <Field
                      as="textarea"
                      name="summary"
                      rows={3}
                      placeholder="Write a brief summary of your article..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
                    />
                    <div className="flex justify-between mt-1">
                      <ErrorMessage name="summary" component="p" className="text-sm text-red-600" />
                      <span className="text-sm text-gray-400">{values.summary?.length || 0}/500</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content * (HTML supported)
                    </label>
                    <Field
                      as="textarea"
                      name="content"
                      rows={15}
                      placeholder="Write your article content here... You can use HTML tags for formatting."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent font-mono text-sm"
                    />
                    <ErrorMessage name="content" component="p" className="mt-1 text-sm text-red-600" />
                    <p className="mt-2 text-sm text-gray-500">
                      Tip: Use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt;, &lt;em&gt; for formatting.
                    </p>
                  </div>

                  {/* Author Info */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Author Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Author Name
                        </label>
                        <Field
                          type="text"
                          name="authorName"
                          placeholder="John Doe"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Author Title
                        </label>
                        <Field
                          type="text"
                          name="authorTitle"
                          placeholder="Certified Nutritionist"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Author Image URL
                        </label>
                        <Field
                          type="url"
                          name="authorImage"
                          placeholder="https://example.com/author.jpg"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Read Time (minutes)
                        </label>
                        <Field
                          type="number"
                          name="readTime"
                          min="1"
                          max="60"
                          placeholder="Auto-calculated if empty"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Publishing Options */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Publishing Options</h3>
                    <div className="flex flex-wrap gap-6">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Field
                          type="checkbox"
                          name="isPublished"
                          className="w-5 h-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                        />
                        <span className="text-gray-700">Publish immediately</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <Field
                          type="checkbox"
                          name="isFeatured"
                          className="w-5 h-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                        />
                        <span className="text-gray-700">Featured article</span>
                      </label>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-4 justify-end">
                    <button
                      type="button"
                      onClick={() => navigate('/blog')}
                      className="px-6 py-3 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || loading}
                      className="flex items-center gap-2 px-8 py-3 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave />
                          {isEditMode ? 'Update Blog' : 'Publish Blog'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddBlog;
